/**
 * File System Access API 存储实现（主方案）
 *
 * Chrome/Edge 桌面完整支持。用户首次选择 Hump 根目录后持久化 handle。
 *
 * 对齐 architecture.md §五「主方案：File System Access API」
 */

import type { Storage } from './index';

/** 获取子目录 handle，不存在则创建 */
async function getDir(
  root: FileSystemDirectoryHandle,
  name: string
): Promise<FileSystemDirectoryHandle> {
  return root.getDirectoryHandle(name, { create: true });
}

/** 获取文件 handle，不存在则创建 */
async function getFile(
  dir: FileSystemDirectoryHandle,
  name: string
): Promise<FileSystemFileHandle> {
  return dir.getFileHandle(name, { create: true });
}

async function readJson<T>(handle: FileSystemFileHandle): Promise<T> {
  const file = await handle.getFile();
  const text = await file.text();
  return JSON.parse(text) as T;
}

async function writeJson(
  handle: FileSystemFileHandle,
  data: unknown
): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(JSON.stringify(data, null, 2));
  await writable.close();
}

export function createFsApiStorage(
  rootHandle: FileSystemDirectoryHandle
): Storage {
  return {
    async readDocIndex(id) {
      const docsDir = await getDir(rootHandle, 'docs');
      const docDir = await getDir(docsDir, id);
      const handle = await getFile(docDir, 'index.hump');
      return readJson(handle);
    },

    async writeDocIndex(index) {
      const docsDir = await getDir(rootHandle, 'docs');
      const docDir = await getDir(docsDir, index.id);
      const handle = await getFile(docDir, 'index.hump');
      await writeJson(handle, index);
    },

    async readBlock(docId, blockId) {
      const docsDir = await getDir(rootHandle, 'docs');
      const docDir = await getDir(docsDir, docId);
      const blocksDir = await getDir(docDir, 'blocks');
      const handle = await getFile(blocksDir, `${blockId}.json`);
      return readJson(handle);
    },

    async writeBlock(docId, block) {
      const docsDir = await getDir(rootHandle, 'docs');
      const docDir = await getDir(docsDir, docId);
      const blocksDir = await getDir(docDir, 'blocks');
      const handle = await getFile(blocksDir, `${block.id}.json`);
      await writeJson(handle, block);
    },

    async listBlocks(docId) {
      const docsDir = await getDir(rootHandle, 'docs');
      const docDir = await getDir(docsDir, docId);
      const blocksDir = await getDir(docDir, 'blocks');
      const ids: string[] = [];
      for await (const [name] of blocksDir.entries()) {
        if (name.endsWith('.json')) ids.push(name.replace('.json', ''));
      }
      return ids.sort();
    },

    async readVocab() {
      const vocabDir = await getDir(rootHandle, 'vocab');
      const handle = await getFile(vocabDir, 'hump.json');
      return readJson(handle);
    },

    async writeVocab(vocab) {
      const vocabDir = await getDir(rootHandle, 'vocab');
      const handle = await getFile(vocabDir, 'hump.json');
      await writeJson(handle, vocab);
    },

    async readLibrary() {
      const handle = await getFile(rootHandle, 'library.json');
      return readJson(handle);
    },

    async writeLibrary(lib) {
      const handle = await getFile(rootHandle, 'library.json');
      await writeJson(handle, lib);
    },

    async deleteDoc(docId) {
      const docsDir = await getDir(rootHandle, 'docs');
      await docsDir.removeEntry(docId, { recursive: true });
    },
  };
}
